import topupSvg from '@raison/static/icons/add-line.svg';
import {default as exchangeSvg} from '@raison/static/icons/arrow-left-right-line.svg';
import receiveSvg from '@raison/static/icons/download-line.svg';
import sellSvg from '@raison/static/icons/subtract-line.svg';
import withdrawSvg from '@raison/static/icons/upload-line.svg';
import {RootLayout, RsnCopyButton, RsnSkeleton, RsnTextButton} from '@raison/ui-kit';
import {useInfiniteQuery} from '@tanstack/react-query';
import {type} from 'arktype';
import * as Array from 'effect/Array';
import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';
import {NotFoundPage} from '~/entities/fallbacks';
import {TransactionsHistory, useTransactionsHistoryOptions} from '~/entities/transactions-history';
import {TransactionDetailsModal, TransactionsList} from '~/features/transitions-list';
import {getKeysUnsafe} from '~/library/toolbox/src';
import {TransactionId} from '~/library/types/src';
import {useAppDispatch} from '~/shared/store/hooks';
import {DashboardActionGroup} from './DashboardActionGroup';

const decodeParams = type({
    assetType: 'string?',
    assetId: 'string',
});

export function AssetDetailsRoute({provider: Provider}: {provider: AssetDetailsProvider}) {
    const params = useParams();
    const {assetId} = useMemo(() => decodeParams.assert(params), []);
    return (
        <Provider assetId={assetId} notFound={<NotFoundPage />}>
            {AssetDetailsLayout}
        </Provider>
    );
}

export function AssetDetailsLayout({Icon, Totals, Details, Actions, History, title}: AssetDetailsParts) {
    return (
        <RootLayout
            title={title}
            background='none'
            pageHeader={<RootLayout.CustomHeader children={<RootLayout.TitleOutlet multiline />} />}
        >
            <div className='flex flex-1 flex-col gap-4'>
                <div className='flex flex-col items-center gap-4 p-4'>
                    <Icon.type {...Icon.props} />
                    <Totals.type {...Totals.props} />
                </div>
                {!!Details && (
                    <div className='mx-4 flex flex-col gap-4 rounded-lg bg-neutral-10 p-4'>
                        <Details.type {...Details.props} />
                    </div>
                )}
                {!!Actions && ( //
                    <Actions.type {...Actions.props} className={'mx-4'} />
                )}
                {!!History && ( //
                    <History.type {...History.props} />
                )}
            </div>
        </RootLayout>
    );
}

export interface AssetDetailsParts {
    title: string;
    Icon: React.JSX.Element;
    Totals: React.JSX.Element;
    Details?: React.JSX.Element;
    Actions?: React.JSX.Element;
    History?: React.JSX.Element;
}

export interface AssetDetailsProvider {
    (props: {
        children: (parts: AssetDetailsParts) => React.JSX.Element;
        assetId: string;
        assetType?: string;
        notFound: React.JSX.Element;
    }): React.JSX.Element;
    matchType?: (accountType: string) => boolean;
}

export function AssetIcon({src, framed}: {src: string; framed?: boolean}) {
    if (framed) {
        return (
            <div className='size-16 rounded-full bg-neutral-30 p-4'>
                <img src={src} alt='Logo' />{' '}
            </div>
        );
    }
    return <img src={src} alt='Logo' className='size-16 rounded-full bg-neutral-30' />;
}
AssetIcon.Skeleton = function AssetIconSkeleton() {
    return <RsnSkeleton circle className='size-16' />;
};

export function AssetTotal(props: {balance: React.ReactNode; details?: React.ReactNode}) {
    return (
        <div className='flex max-w-full flex-col gap-1 text-center'>
            <strong className='fv-heading-l text-neutral-100'>{props.balance}</strong>
            {!!props.details && <small className='fv-text-l-regular text-neutral-100'>{props.details}</small>}
        </div>
    );
}
AssetTotal.Skeleton = function AssetTotalSkeleton(props: {withDetails?: boolean}) {
    return AssetTotal({
        balance: <RsnSkeleton width={180} />,
        ...(props.withDetails ? {details: <RsnSkeleton width={90} />} : null),
    });
};

export interface AssetDetailEntry {
    label: string | React.ReactElement;
    value: string | React.ReactElement;
    copy?: boolean;
}

export namespace AssetDetailEntry {
    export type Optional = Omit<AssetDetailEntry, 'value'> & {
        value?: AssetDetailEntry['value'] | undefined | null;
    };

    export const isNotOptional = (entry: AssetDetailEntry.Optional): entry is AssetDetailEntry =>
        'value' in entry && !!entry.value;
}

export function AssetDetails(props: {sections: AssetDetailEntry[][]}) {
    const isEmpty = props.sections.every(Array.isEmptyArray);
    if (isEmpty) {
        return <div className='fv-text-xl-regular p-2 text-center text-neutral-60'>No details available</div>;
    }
    return (
        <table className='flex flex-col divide-y divide-dashed divide-neutral-20'>
            {props.sections.map((section, i) => (
                <tbody key={i} className='flex flex-col gap-2 py-4 first:pt-0 last:pb-0'>
                    {section.map(({label, value, ...entry}, i) => (
                        <tr key={i} className='fv-text-l-regular flex justify-between gap-4'>
                            <td align='left' className='text-nowrap text-neutral-60'>
                                {label}
                            </td>
                            <td
                                align='right'
                                className='flex items-center gap-2 text-neutral-100 [word-break:break-word]'
                            >
                                {entry.copy && typeof value === 'string' ? (
                                    <label className='flex items-center gap-2'>
                                        {value}
                                        <RsnCopyButton value={value} size={16} />
                                    </label>
                                ) : (
                                    value
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            ))}
        </table>
    );
}
AssetDetails.Skeleton = function AssetDetailsSkeleton({sections = [4, 1]}: {sections?: number[]}) {
    const randomWidth = () => Math.round(Math.random() * 40 + 40);
    return AssetDetails({
        sections: sections.map(numberOfLines =>
            Array.makeBy(numberOfLines, () => ({
                label: <RsnSkeleton width={randomWidth()} />,
                value: <RsnSkeleton width={randomWidth()} />,
            }))
        ),
    });
};

export function AssetHistory({filters}: {filters: TransactionsHistory.Filters}) {
    const {
        data: {orders = []} = {},
        isFetching,
        isLoading,
        isError,
        refetch,
    } = useInfiniteQuery({
        ...useTransactionsHistoryOptions({...filters, limit: 5}),
        select({pages: [firstPage]}) {
            return firstPage;
        },
        refetchOnMount: true,
    });
    const navigate = useNavigate();

    const {txnId: _txnId, ...restLocState} = useLocation()?.state ?? {};
    const txnId = isNaN(_txnId) ? undefined : (Number(_txnId) as TransactionId);
    const transactionFromList = orders.find(txn => txn.id === txnId);

    return (
        <div className='-mb-4 flex flex-1 shrink-0 flex-col rounded-t-lg bg-neutral-10 pb-4 pt-4'>
            <div className='fv-heading-s flex justify-between px-4 pb-4 text-neutral-100'>
                <h3 className='[font:inherit]'>Last transactions</h3>
                <RsnTextButton.Inline
                    onClick={() => {
                        navigate(`/transactions?${TransactionsHistory.filtersToSearch(filters)}`);
                    }}
                    size='xl'
                >
                    View all
                </RsnTextButton.Inline>
            </div>

            <TransactionsList
                orders={orders}
                hasFilters={false}
                isFetching={isFetching}
                isLoading={isLoading}
                isError={isError}
                totalLength={5}
                currentLength={orders.length}
                refetch={refetch}
                onOrderClick={order => {
                    navigate('', {state: {...restLocState, txnId: order.id}});
                }}
            >
                {{
                    zeroActivity: renderFallback,
                    nothingFound: renderFallback,
                }}
            </TransactionsList>
            <TransactionDetailsModal transactionId={txnId} transaction={transactionFromList} />
        </div>
    );

    function renderFallback() {
        return (
            <div className='grid h-full place-items-center'>
                <div className='fv-text-xl-regular text-neutral-60'>You don’t have any activity yet</div>
            </div>
        );
    }
}

const actionsMap = {
    // order matters for crypto operations
    receive: {label: 'Receive', icon: receiveSvg},
    buy: {label: 'Buy', icon: topupSvg},
    sell: {label: 'Sell', icon: sellSvg},
    send: {label: 'Send', icon: withdrawSvg},
    // order matters for fiat operations
    topup: {label: 'Top up', icon: topupSvg},
    exchange: {label: 'Exchange', icon: exchangeSvg},
    withdraw: {label: 'Withdraw', icon: withdrawSvg},
};

export type AssetActionType = keyof typeof actionsMap;

export type AssetActionsRecord = {
    [K in AssetActionType]?: AppAction | undefined;
};

export function AssetActions(props: {className?: string; actions: AssetActionsRecord}) {
    const dispatch = useAppDispatch();
    return (
        <DashboardActionGroup
            className={props.className}
            actions={getKeysUnsafe(actionsMap)
                .filter(actionType => props.actions[actionType] !== undefined)
                .map(actionType => ({
                    onClick: () => dispatch(props.actions[actionType]!),
                    label: actionsMap[actionType].label,
                    icon: actionsMap[actionType].icon,
                }))}
        />
    );
}
